import React from 'react';

export function sortDeckLeaderboardMembers(members, verseCount) {
  const total = Math.max(1, verseCount || 0);
  return [...members].sort((first, second) => {
    const learnedDifference = (second.learned_count || 0) / total
      - (first.learned_count || 0) / total;
    if (learnedDifference !== 0) {
      return learnedDifference;
    }
    return (second.retained_count || 0) / total
      - (first.retained_count || 0) / total;
  });
}

export function isRetentionStale(member, now = Date.now()) {
  return !Number.isFinite(member?.retained_next_expiry_at)
    ? (member?.retained_count || 0) > 0
    : member.retained_next_expiry_at <= now;
}

function formatAsOf(timestamp) {
  return Number.isFinite(timestamp)
    ? new Date(timestamp).toLocaleTimeString()
    : 'an earlier session';
}

export function DeckLeaderboardTab({
  members,
  verseCount,
  loading = false,
  error = '',
  refreshedAt,
}) {
  const now = Date.now();
  const sortedMembers = sortDeckLeaderboardMembers(members, verseCount);
  const percentage = value => Math.min(
    100,
    Math.round(((value || 0) / Math.max(1, verseCount)) * 100)
  );

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '16px' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <h2 style={{ marginTop: 0 }}>Leaderboard</h2>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
          Learned totals are derived from each member's saved memorization work.
          Retained totals are a snapshot from each member's last session.
        </p>
        <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
          Refreshed {refreshedAt ? refreshedAt.toLocaleTimeString() : 'just now'}
        </p>
        {loading && (
          <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <md-circular-progress indeterminate></md-circular-progress>
            Loading members…
          </div>
        )}
        {error && <p role="alert">{error}</p>}
        {sortedMembers.map((member, index) => {
          const learned = percentage(member.learned_count);
          const retained = percentage(member.retained_count);
          const retentionStale = isRetentionStale(member, now);
          return (
            <md-elevated-card
              key={member.id}
              style={{ display: 'block', padding: '16px', marginBottom: '12px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <strong>{index + 1}. {member.displayName}</strong>
                <span>{learned}% learned · {retained}% retained</span>
              </div>
              {retentionStale && (
                <p style={{
                  margin: '8px 0 0',
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}>
                  Retained as of {formatAsOf(member.progress_updated_at)}; some
                  reviews are now due.
                </p>
              )}
              <div
                aria-label={`${member.displayName}: ${learned}% learned, ${retained}% retained${
                  retentionStale ? ' as of an earlier session' : ''
                }`}
                style={{
                  height: '14px',
                  marginTop: '12px',
                  borderRadius: '7px',
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: 'var(--md-sys-color-surface-container-highest)',
                }}
              >
                <div style={{
                  width: `${learned}%`,
                  height: '100%',
                  backgroundColor: 'var(--md-sys-color-primary-container)',
                }} />
                <div style={{
                  width: `${retained}%`,
                  height: '100%',
                  position: 'absolute',
                  inset: 0,
                  opacity: retentionStale ? 0.5 : 1,
                  backgroundColor: 'var(--md-sys-color-primary)',
                }} />
              </div>
            </md-elevated-card>
          );
        })}
        {!loading && !error && sortedMembers.length === 0 && <p>No members yet.</p>}
      </div>
    </div>
  );
}
